import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Masthead } from '@/components/Masthead';
import { Text } from '@/components/Text';
import { Constrain } from '@/components/Constrain';
import { LoadingOverlay } from '@/components/LoadingOverlay';
import { Distribute } from '@/components/Distribute';
import { ButtonLink } from '@/components/Button';
import PlusIcon from '@/icons/Plus';
import { ClientListItem } from '@/components/ClientListItem';
import { EmptyHomeDashboardCallouts } from '@/components/EmptyStates/EmptyHomeDashboardCallouts';
import Block from '@shared/components/ui/Block';
import Switch from '@/components/Switch';
import { useCurrentUser } from '@/api/user';
import { useClients, type Client } from '@/api/clients';
import { useAddeparIntegration } from '@/api/integration';
import { usePrivateMode } from '@/hooks/usePrivateMode';

/**
 * Port of the old `_boundary._index.tsx` home dashboard. Loaders →
 * useCurrentUser/useClients/useAddeparIntegration; the privateMode cookie +
 * action → usePrivateMode (localStorage).
 */
export function HomePage() {
    const userQuery = useCurrentUser();
    const clientsQuery = useClients();
    const { data: addeparIntegration } = useAddeparIntegration();
    const { isPrivateMode, togglePrivateMode } = usePrivateMode();

    const [clientListFilter, setClientListFilter] = useState('');

    const handleSearchFilterChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setClientListFilter(event.target.value.toLowerCase());
        },
        []
    );

    const hadSetupAddeparIntegration = addeparIntegration?.status === 'ENABLED';

    // Surface backend failures to the error boundary instead of looping.
    if (clientsQuery.isError || userQuery.isError) {
        throw new Error('Could not load your dashboard');
    }

    const user = userQuery.data;
    const clients = clientsQuery.data;

    if (clientsQuery.isLoading || userQuery.isLoading || !clients || !user) {
        return (
            <>
                <Masthead $noMargin />
                <LoadingOverlay />
            </>
        );
    }

    const filteredClients = clients.filter((client) =>
        client.name?.toLowerCase().includes(clientListFilter)
    );

    return (
        <>
            <Masthead $noMargin />

            <ClientsArea>
                <Constrain width="narrow">
                    <Distribute
                        $vAlign="center"
                        spacing="xLarge"
                        style={clients.length === 0 ? { opacity: 0.5 } : {}}
                    >
                        <Text as="h2" size="xLarge" font="primary" weight="bold">
                            Your clients
                        </Text>

                        <Actions>
                            <Block
                                $stack="horizontal"
                                $verticalAlign="center"
                                $gap={8}
                            >
                                <Text size="small" weight="bold">
                                    Private mode
                                </Text>

                                <Switch
                                    id="private-mode-toggle"
                                    toggled={isPrivateMode}
                                    onChange={togglePrivateMode}
                                />
                            </Block>

                            {!hadSetupAddeparIntegration && (
                                <ButtonLink
                                    to="/settings/user"
                                    $variant="icon"
                                    size="medium"
                                    disabled={clients.length === 0}
                                >
                                    Manage integrations{' '}
                                    <PlusIcon strokeColor="white" />
                                </ButtonLink>
                            )}

                            {hadSetupAddeparIntegration && (
                                <ButtonLink
                                    to="/settings/user?open=addepar"
                                    $variant="icon"
                                    size="medium"
                                    disabled={clients.length === 0}
                                >
                                    Manage Addepar clients{' '}
                                    <PlusIcon strokeColor="white" />
                                </ButtonLink>
                            )}

                            <ButtonLink
                                to="/client/create"
                                $variant="icon"
                                size="medium"
                                disabled={clients.length === 0}
                            >
                                Create client <PlusIcon strokeColor="white" />
                            </ButtonLink>
                        </Actions>
                    </Distribute>

                    <Block spacing="xLarge">
                        <FilterInput
                            name="name"
                            placeholder={
                                isPrivateMode
                                    ? 'Search for clients by name, name will show when one is found'
                                    : 'Search for clients by name'
                            }
                            type="search"
                            value={clientListFilter}
                            onChange={handleSearchFilterChange}
                            disabled={clients.length === 0}
                        />
                    </Block>

                    {clients.length === 0 ? (
                        <EmptyHomeDashboardCallouts />
                    ) : (
                        filteredClients.map((client) => (
                            <Block spacing="medium" key={client.clientReference}>
                                <ClientListItem
                                    client={client as Client}
                                    isPrivateMode={
                                        isPrivateMode && filteredClients.length > 1
                                    }
                                />
                            </Block>
                        ))
                    )}
                </Constrain>
            </ClientsArea>
        </>
    );
}

const ClientsArea = styled.div`
    padding: 30px 0px;
`;

const Actions = styled.div`
    display: inline-flex;
    gap: 16px;
`;

const FilterInput = styled.input`
    width: 100%;
    padding: 8px 12px;
    color: ${({ theme }) => theme.colors.white};
    font-family: ${({ theme }) => theme.fonts.secondary.regular};
    background: ${({ theme }) => theme.colors.white};
    border-radius: 8px;
    border: solid 1px ${({ theme }) => theme.colors.uNavy10};
    appearance: none;

    &::placeholder {
        color: ${({ theme }) => theme.colors.darkGrey};
    }

    &:focus {
        outline: none;
    }
`;
